/**
 * Organization API Service
 *
 * Provides API integration for Organization, Team, and Team Member Management
 * Backend Endpoints: /api/core/organizations
 *
 * CRITICAL NOTES:
 * - All API calls MUST include X-User-Id, X-Organization-Id, X-Tenant-Id headers
 * - JWT Bearer token required in Authorization header
 * - Backend returns camelCase, frontend expects camelCase (no mapping needed)
 * - Follow the employee/assessment API pattern for error handling and request structure
 *
 * @author Tutara Team
 * @since January 2025
 */

import { API_BASE_URL } from '@/config/env'
import { useAuthStore } from '@/stores/auth-store';
import type {
  OrganizationRequest,
  OrganizationUpdateRequest,
  OrganizationResponse,
  OrganizationStatsResponse,
  OrganizationSettings,
  TeamRequest,
  TeamUpdateRequest,
  TeamResponse,
  TeamMemberRequest,
  TeamMemberResponse,
  PageResponse,
  OrganizationQueryParams,
  TeamQueryParams,
  TeamMemberQueryParams,
  ApiErrorResponse,
} from './types';

// =============================================================================
// Configuration
// =============================================================================

const ORGANIZATION_API_BASE = `${API_BASE_URL}/api/core/organizations`;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get authentication headers from auth store
 * CRITICAL: Must include X-User-Id, X-Organization-Id, X-Tenant-Id for multi-tenant support
 */
function getAuthHeaders(): Record<string, string> {
  // Get tokens and user from Zustand store
  const { tokens, user } = useAuthStore.getState();

  if (!tokens?.accessToken) {
    throw new Error('Not authenticated');
  }

  // Extract user and organization context
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${tokens.accessToken}`,
    'Content-Type': 'application/json',
  };

  // Add user ID header (required)
  if (user?.id) {
    headers['X-User-Id'] = user.id;
  }

  // Add organization ID if available (required for most operations)
  if (user?.organizationId) {
    headers['X-Organization-Id'] = user.organizationId;
  }

  // Add tenant ID if available (for multi-tenancy support)
  if (user?.tenantId) {
    headers['X-Tenant-Id'] = user.tenantId;
  }

  return headers;
}

/**
 * Build query string from parameters
 */
function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Request helper with comprehensive error handling
 * Follows the pattern from auth/api.ts and assessment/api.ts
 */
async function fetchWithErrorHandling<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });

    // Handle 204 No Content response
    if (response.status === 204) {
      return {} as T;
    }

    // Parse JSON response
    const data = await response.json();

    // Handle error responses
    if (!response.ok) {
      const errorData = data as ApiErrorResponse;
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data as T;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

// =============================================================================
// Organization API Methods
// =============================================================================

/**
 * Get all organizations with optional filters
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of organizations
 *
 * @example
 * const orgs = await getOrganizations({ status: 'active', page: 0, size: 20 });
 */
export async function getOrganizations(
  params: OrganizationQueryParams = {}
): Promise<PageResponse<OrganizationResponse>> {
  const queryString = buildQueryString(params);
  return fetchWithErrorHandling<PageResponse<OrganizationResponse>>(
    `${ORGANIZATION_API_BASE}${queryString}`,
    { method: 'GET' }
  );
}

/**
 * Get organization by ID
 *
 * @param organizationId - Organization UUID
 * @returns Organization details
 *
 * @example
 * const org = await getOrganizationById('org-uuid-123');
 */
export async function getOrganizationById(
  organizationId: string
): Promise<OrganizationResponse> {
  return fetchWithErrorHandling<OrganizationResponse>(
    `${ORGANIZATION_API_BASE}/${organizationId}`,
    { method: 'GET' }
  );
}

/**
 * Create a new organization
 *
 * @param request - Organization creation data
 * @returns Created organization
 *
 * @example
 * const org = await createOrganization({
 *   name: 'Acme Corp',
 *   tenantId: 'tenant-uuid',
 *   contactEmail: 'admin@acme.com'
 * });
 */
export async function createOrganization(
  request: OrganizationRequest
): Promise<OrganizationResponse> {
  return fetchWithErrorHandling<OrganizationResponse>(
    ORGANIZATION_API_BASE,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Update an organization
 *
 * @param organizationId - Organization UUID
 * @param request - Partial update data
 * @returns Updated organization
 *
 * @example
 * const org = await updateOrganization('org-uuid', { name: 'New Name' });
 */
export async function updateOrganization(
  organizationId: string,
  request: OrganizationUpdateRequest
): Promise<OrganizationResponse> {
  return fetchWithErrorHandling<OrganizationResponse>(
    `${ORGANIZATION_API_BASE}/${organizationId}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Delete an organization (soft delete)
 *
 * @param organizationId - Organization UUID
 *
 * @example
 * await deleteOrganization('org-uuid');
 */
export async function deleteOrganization(
  organizationId: string
): Promise<void> {
  return fetchWithErrorHandling<void>(
    `${ORGANIZATION_API_BASE}/${organizationId}`,
    { method: 'DELETE' }
  );
}

/**
 * Get organization settings
 *
 * @param organizationId - Organization UUID
 * @returns Organization settings
 *
 * @example
 * const settings = await getOrganizationSettings('org-uuid');
 */
export async function getOrganizationSettings(
  organizationId: string
): Promise<OrganizationSettings> {
  const org = await getOrganizationById(organizationId);
  return org.settings || {};
}

/**
 * Update organization settings (SSO, branding, integrations)
 *
 * @param organizationId - Organization UUID
 * @param settings - Updated settings
 * @returns Updated organization
 *
 * @example
 * const org = await updateOrganizationSettings('org-uuid', {
 *   ssoEnabled: true,
 *   customBranding: true,
 *   integrations: { confluence: true, slack: true }
 * });
 */
export async function updateOrganizationSettings(
  organizationId: string,
  settings: OrganizationSettings
): Promise<OrganizationResponse> {
  return updateOrganization(organizationId, { settings });
}

/**
 * Get organization statistics
 *
 * @param organizationId - Organization UUID
 * @returns Organization statistics
 *
 * @example
 * const stats = await getOrganizationStats('org-uuid');
 * console.log(`Active users: ${stats.activeUsers}/${stats.totalUsers}`);
 */
export async function getOrganizationStats(
  organizationId: string
): Promise<OrganizationStatsResponse> {
  return fetchWithErrorHandling<OrganizationStatsResponse>(
    `${ORGANIZATION_API_BASE}/${organizationId}/stats`,
    { method: 'GET' }
  );
}

// =============================================================================
// Team API Methods
// =============================================================================

/**
 * Get all teams for an organization
 *
 * @param organizationId - Organization UUID
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of teams
 *
 * @example
 * const teams = await getTeams('org-uuid', { page: 0, size: 20 });
 */
export async function getTeams(
  organizationId: string,
  params: TeamQueryParams = {}
): Promise<PageResponse<TeamResponse>> {
  const queryString = buildQueryString(params);
  return fetchWithErrorHandling<PageResponse<TeamResponse>>(
    `${ORGANIZATION_API_BASE}/${organizationId}/teams${queryString}`,
    { method: 'GET' }
  );
}

/**
 * Get team by ID
 *
 * @param organizationId - Organization UUID
 * @param teamId - Team UUID
 * @returns Team details
 *
 * @example
 * const team = await getTeamById('org-uuid', 'team-uuid');
 */
export async function getTeamById(
  organizationId: string,
  teamId: string
): Promise<TeamResponse> {
  return fetchWithErrorHandling<TeamResponse>(
    `${ORGANIZATION_API_BASE}/${organizationId}/teams/${teamId}`,
    { method: 'GET' }
  );
}

/**
 * Create a new team
 *
 * @param organizationId - Organization UUID
 * @param request - Team creation data
 * @returns Created team
 *
 * @example
 * const team = await createTeam('org-uuid', {
 *   organizationId: 'org-uuid',
 *   tenantId: 'tenant-uuid',
 *   name: 'Engineering',
 *   teamType: 'department'
 * });
 */
export async function createTeam(
  organizationId: string,
  request: TeamRequest
): Promise<TeamResponse> {
  return fetchWithErrorHandling<TeamResponse>(
    `${ORGANIZATION_API_BASE}/${organizationId}/teams`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Update a team
 *
 * @param organizationId - Organization UUID
 * @param teamId - Team UUID
 * @param request - Partial update data
 * @returns Updated team
 *
 * @example
 * const team = await updateTeam('org-uuid', 'team-uuid', { name: 'New Name' });
 */
export async function updateTeam(
  organizationId: string,
  teamId: string,
  request: TeamUpdateRequest
): Promise<TeamResponse> {
  return fetchWithErrorHandling<TeamResponse>(
    `${ORGANIZATION_API_BASE}/${organizationId}/teams/${teamId}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Delete a team (soft delete)
 *
 * @param organizationId - Organization UUID
 * @param teamId - Team UUID
 *
 * @example
 * await deleteTeam('org-uuid', 'team-uuid');
 */
export async function deleteTeam(
  organizationId: string,
  teamId: string
): Promise<void> {
  return fetchWithErrorHandling<void>(
    `${ORGANIZATION_API_BASE}/${organizationId}/teams/${teamId}`,
    { method: 'DELETE' }
  );
}

// =============================================================================
// Team Member API Methods
// =============================================================================

/**
 * Get team members
 *
 * @param teamId - Team UUID
 * @param params - Query parameters for filtering and pagination
 * @returns Paginated list of team members
 *
 * @example
 * const members = await getTeamMembers('team-uuid', { page: 0, size: 20 });
 */
export async function getTeamMembers(
  teamId: string,
  params: TeamMemberQueryParams = {}
): Promise<PageResponse<TeamMemberResponse>> {
  const queryString = buildQueryString(params);
  return fetchWithErrorHandling<PageResponse<TeamMemberResponse>>(
    `${ORGANIZATION_API_BASE}/teams/${teamId}/members${queryString}`,
    { method: 'GET' }
  );
}

/**
 * Add member to team
 *
 * @param teamId - Team UUID
 * @param request - Team member data
 * @returns Created team member
 *
 * @example
 * const member = await addTeamMember('team-uuid', {
 *   teamId: 'team-uuid',
 *   userId: 'user-uuid',
 *   role: 'member'
 * });
 */
export async function addTeamMember(
  teamId: string,
  request: TeamMemberRequest
): Promise<TeamMemberResponse> {
  return fetchWithErrorHandling<TeamMemberResponse>(
    `${ORGANIZATION_API_BASE}/teams/${teamId}/members`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

/**
 * Remove member from team
 *
 * @param teamId - Team UUID
 * @param userId - User UUID to remove
 *
 * @example
 * await removeTeamMember('team-uuid', 'user-uuid');
 */
export async function removeTeamMember(
  teamId: string,
  userId: string
): Promise<void> {
  return fetchWithErrorHandling<void>(
    `${ORGANIZATION_API_BASE}/teams/${teamId}/members/${userId}`,
    { method: 'DELETE' }
  );
}

// =============================================================================
// User Organization API Methods
// =============================================================================

/**
 * Switch user's current organization context
 *
 * @param organizationId - Organization UUID to switch to
 * @returns Success response
 *
 * @example
 * await switchOrganization('org-uuid-456');
 */
export async function switchOrganization(
  organizationId: string
): Promise<void> {
  return fetchWithErrorHandling<void>(
    `${API_BASE_URL}/api/core/mt/users/switch-organization`,
    {
      method: 'POST',
      body: JSON.stringify({ organizationId }),
    }
  );
}

/**
 * Get user's organizations
 *
 * @returns List of organizations user belongs to
 *
 * @example
 * const orgs = await getUserOrganizations();
 */
export async function getUserOrganizations(): Promise<OrganizationResponse[]> {
  return fetchWithErrorHandling<OrganizationResponse[]>(
    `${API_BASE_URL}/api/core/mt/users/me/organizations`,
    { method: 'GET' }
  );
}

// =============================================================================
// Exports
// =============================================================================

export const organizationApi = {
  // Organization methods
  getOrganizations,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  getOrganizationSettings,
  updateOrganizationSettings,
  getOrganizationStats,

  // Team methods
  getTeams,
  getTeamById,
  createTeam,
  updateTeam,
  deleteTeam,

  // Team member methods
  getTeamMembers,
  addTeamMember,
  removeTeamMember,

  // User organization methods
  switchOrganization,
  getUserOrganizations,
};
